'use client';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState, useEffect, useCallback } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

import ChevronIcon from '@/fo/icons/base/icons/chevron24.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitDialogBase } from '@/fo/ui/kit/dialog/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';

import { Thumb } from '../components/Thumb/Thumb';

import { styles } from './UiKitLightboxBase.styles';

export interface Image {
  id: string | number;
  type: 'image' | 'video';
  src: string;
  alt: string;
}

export interface UiKitLightboxBaseProps {
  className?: string;
  images: Image[];
  open: boolean;
  onOpenChange?: () => void;
  initialIndexSlide?: number;
}

const imageSize = 720;

export function UiKitLightboxBase({
  className,
  images,
  open,
  onOpenChange,
  initialIndexSlide = 0
}: UiKitLightboxBaseProps) {
  const t = useTranslations();

  const slots = styles();
  const isDesktop = useMediaQuery('lg');
  const [selectedIndex, setSelectedIndex] = useState(initialIndexSlide);
  const [isZoom, setIsZoom] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      axis: isDesktop ? 'y' : 'x',
      dragFree: true,
      align: isDesktop ? 'center' : 'start',
      containScroll: false
    },
    [WheelGesturesPlugin()]
  );
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    axis: isDesktop ? 'y' : 'x'
  });

  const onThumbClick = (index: number) => {
    if (!emblaApi) return;

    emblaApi.scrollTo(index);
  };

  const onSelect = useCallback(() => {
    if (!emblaApi || !emblaThumbsApi) return;

    setSelectedIndex(emblaApi.selectedScrollSnap());

    emblaApi.scrollTo(emblaApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaApi.selectedScrollSnap());
  }, [emblaApi, emblaThumbsApi]);

  useEffect(() => {
    if (!emblaApi || !emblaThumbsApi) return;
    onSelect();

    emblaApi.on('select', onSelect).on('reInit', onSelect);

    emblaApi.scrollTo(selectedIndex);
    emblaThumbsApi.scrollTo(selectedIndex);
  }, [emblaApi, emblaThumbsApi, onSelect, selectedIndex]);

  useEffect(() => {
    emblaApi?.reInit({ watchDrag: !isZoom });
  }, [emblaApi, isZoom]);

  return (
    <UiKitDialogBase
      open={open}
      onOpenChange={onOpenChange}
      className={slots.base({ className })}
      fullScreen
      buttonClassName={slots.closeButton()}
      title={t('detail-view-image')}
    >
      {images.length > 1 && (
        <div className={slots.thumbs({ zoom: isZoom })}>
          <UiKitButtonBase
            onClick={() => emblaApi?.scrollPrev()}
            className={slots.thumbArrow({
              className: '-rotate-90',
              hide: !emblaApi?.canScrollPrev() || images.length <= 5
            })}
            rounded="full"
            color="outlined"
          >
            <ChevronIcon />
          </UiKitButtonBase>

          <div
            ref={emblaThumbsRef}
            className={slots.thumbsViewport()}
          >
            <div className={slots.thumbsContainer()}>
              {images.map((image: Image, index) => (
                <Thumb
                  key={index}
                  onClick={() => onThumbClick(index)}
                  selected={index === selectedIndex}
                >
                  {image.type === 'video' && (
                    <video
                      className={slots.video()}
                      width={80}
                      height={80}
                    >
                      <source
                        src={image.src}
                        type="video/mp4"
                      />
                    </video>
                  )}
                  {image.type === 'image' && (
                    <Image
                      className={slots.thumb()}
                      src={image.src}
                      alt=""
                      width={80}
                      height={80}
                    />
                  )}
                </Thumb>
              ))}
            </div>
          </div>

          <UiKitButtonBase
            onClick={() => emblaApi?.scrollNext()}
            className={slots.thumbArrow({
              className: 'h-6 rotate-90',
              hide: !emblaApi?.canScrollNext() || images.length <= 5
            })}
            rounded="full"
            color="outlined"
          >
            <ChevronIcon />
          </UiKitButtonBase>
        </div>
      )}

      <div
        className={slots.viewport({ zoom: isZoom })}
        ref={emblaRef}
      >
        <div className={slots.container()}>
          {images.map((image: Image, index) => (
            <div
              className={slots.imageWrapper({ zoom: isZoom })}
              key={index}
              onClick={() => setIsZoom(!isZoom && !isDesktop)}
            >
              {image.type === 'video' && (
                <video
                  className={slots.video()}
                  autoPlay
                  muted
                  width={imageSize}
                  height={imageSize}
                >
                  <source
                    src={image.src}
                    type="video/mp4"
                  />
                </video>
              )}
              {image.type === 'image' && (
                <TransformWrapper
                  doubleClick={{ mode: 'toggle' }}
                  wheel={{ disabled: true }}
                  disablePadding={true}
                  maxScale={1.5}
                  onPanningStart={() => {
                    if (isDesktop) emblaApi?.reInit({ watchDrag: false });
                  }}
                  onPanningStop={() => {
                    if (isDesktop) emblaApi?.reInit({ watchDrag: true });
                  }}
                  onZoomStart={() => {
                    if (!isDesktop) setIsZoom(!isZoom);
                  }}
                  panning={{
                    disabled: !isZoom && !isDesktop
                  }}
                >
                  <TransformComponent
                    wrapperStyle={{ height: '100%', width: '100%' }}
                    contentStyle={{ height: '100%', width: '100%' }}
                  >
                    <Image
                      className={slots.image({ zoom: isZoom })}
                      src={image.src}
                      alt=""
                      width={imageSize}
                      height={imageSize}
                    />
                  </TransformComponent>
                </TransformWrapper>
              )}
            </div>
          ))}
        </div>
      </div>
    </UiKitDialogBase>
  );
}
